import { Button } from '@/components/buttons';
import { Input } from '@/components/inputs';
import { Select } from '@/components/selects';
import { TrackClickEvents, TrackPageVisitEvents, TrackWebsiteValue } from '@/features/dataSources/components/createDataSource/types';
import { ISelectItem } from '@/models/select';
import { XIcon } from '@heroicons/react/solid';
import { FieldArray, FieldArrayRenderProps, FormikErrors, getIn } from 'formik';
import { isEmpty } from 'lodash';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';
import { AddButton } from '../AddButton';
import { DataSourceSelector } from '@/models/dataSource';

type TrackUserClickProps = {
  values: TrackWebsiteValue;
  errors: FormikErrors<TrackWebsiteValue>;
  handleSetFieldValue: (field: string, value: TrackClickEvents | TrackPageVisitEvents) => void;
  handleSetFieldError: (field: string, message: string | undefined) => void;
  handleDeleteRule: (ruleId: string) => void;
};

export const TrackUserClick: FC<TrackUserClickProps> = ({ values, handleSetFieldValue, handleSetFieldError, handleDeleteRule, errors }) => {
  const { t } = useTranslation('data_source_edit');

  const selectItems: ISelectItem[] = [
    { id: DataSourceSelector.ID, label: t('code.track_events.clicks_on_id') },
    { id: DataSourceSelector.CLASS, label: t('code.track_events.clicks_on_class') },
  ];

  const handleOnAddClicked = (clickEventHelper: FieldArrayRenderProps) => {
    const identifier = uuidv4();
    clickEventHelper.push({ identifier, selectorType: DataSourceSelector.ID, selectorName: '', eventName: '' });
  };

  const handleDeleteEvent = (clickEventHelper: FieldArrayRenderProps, index: number, ruleId?: string) => {
    if (ruleId) {
      handleDeleteRule(ruleId);
    }
    clickEventHelper.remove(index);
  };

  return (
    <div>
      <FieldArray
        name='clickEvents'
        validateOnChange={false}
        render={(clickEventHelper) => (
          <div>
            {clickEventHelper &&
              !isEmpty(clickEventHelper) &&
              values.clickEvents.map((event, index) => (
                <div key={index} className='flex px-4 my-2'>
                  <div className='flex w-1/2'>
                    <Select
                      items={selectItems}
                      value={selectItems[event.selectorType]}
                      className='ml-1 w-52'
                      disabled={event.id !== undefined}
                      onChange={(value) => {
                        handleSetFieldValue(`clickEvents.${index}`, {
                          ...event,
                          selectorType: value.id === DataSourceSelector.ID ? DataSourceSelector.ID : DataSourceSelector.CLASS,
                        });
                      }}
                    />
                    <div className='relative flex-auto'>
                      <span className={twMerge('absolute z-10 text-gray-400', event.selectorType === DataSourceSelector.ID ? 'mt-2 ml-2.5' : 'mt-2 ml-4')}>
                        {event.selectorType === DataSourceSelector.ID ? '#' : '.'}
                      </span>
                      <Input
                        className='mx-1 flex-auto'
                        placeholder={event.selectorType === DataSourceSelector.ID ? t('code.track_events.name_of_the_id') : t('code.track_events.name_of_the_class')}
                        value={event.selectorName}
                        onChange={(e) => {
                          handleSetFieldError(`clickEvents.${index}.selectorName`, undefined);
                          handleSetFieldValue(`clickEvents.${index}`, { ...event, selectorName: e.target.value });
                        }}
                        disabled={event.id !== undefined}
                        error={errors.clickEvents && errors.clickEvents[index] ? getIn(errors.clickEvents[index], 'selectorName') : null}
                      />
                    </div>
                  </div>
                  <div className='flex w-1/2'>
                    <span className='py-2 ml-2 mr-1'>{t('code.track_events.creates')}</span>
                    <div className='flex-auto'>
                      <Input
                        placeholder={t('code.track_events.name_of_the_event')}
                        value={event.eventName}
                        onChange={(e) => {
                          handleSetFieldError(`clickEvents.${index}.eventName`, undefined);
                          handleSetFieldValue(`clickEvents.${index}`, {
                            ...event,
                            eventName: e.target.value,
                          });
                        }}
                        disabled={event.id !== undefined}
                        error={errors.clickEvents && errors.clickEvents[index] ? getIn(errors.clickEvents[index], 'eventName') : null}
                      />
                    </div>
                    <Button variant='transparent' type='button' className='flex-0 ml-1' onClick={() => handleDeleteEvent(clickEventHelper, index, event.id)}>
                      <XIcon className='h-6 w-6 text-gray-500' aria-hidden='true' />
                    </Button>
                  </div>
                </div>
              ))}
            <div className='bg-white py-6 px-5 my-auto'>
              <AddButton label={t('code.track_events.buttons.add_event')} onClick={() => handleOnAddClicked(clickEventHelper)} />
            </div>
          </div>
        )}
      />
    </div>
  );
};
